import { z } from 'zod';
import { bundleFeedbackSchema, NO_FEEDBACK } from './feedback-prompt.schema.js';
import { publishedGuidanceSchema } from './guidance.schema.js';

/**
 * Major version of the bundle structure (technical §16.9). A SDK receiving a
 * greater version falls back to capture only rather than interpreting an
 * unknown structure.
 */
export const BUNDLE_SCHEMA_VERSION = 1;

/** Subsystems the platform may switch on or off for a project (technical §11.4). */
export const BUNDLE_SUBSYSTEMS = ['core', 'capture', 'guidance', 'triggering', 'feedback'] as const;
export type BundleSubsystem = (typeof BUNDLE_SUBSYSTEMS)[number];

export const bundleSubsystemsSchema = z
  .object({
    core: z.boolean(),
    capture: z.boolean(),
    guidance: z.boolean(),
    triggering: z.boolean(),
    feedback: z.boolean(),
  })
  .strict();
export type BundleSubsystems = z.infer<typeof bundleSubsystemsSchema>;

/** Every subsystem on: what a published release carries. */
export const ALL_SUBSYSTEMS_ON: BundleSubsystems = {
  core: true,
  capture: true,
  guidance: true,
  triggering: true,
  feedback: true,
};

/** The whole layer off but the core: served while a project is suspended (module M1). */
export const SUSPENDED_SUBSYSTEMS: BundleSubsystems = {
  core: true,
  capture: false,
  guidance: false,
  triggering: false,
  feedback: false,
};

/**
 * Read aloud by the platform (ADR 0018): the audio of a published text is made
 * on the server, so it plays the same on every system. A platform setting, not
 * release content: set when the bundle is served, off on a bundle stored without it.
 */
export const bundleSpeechSchema = z.object({ enabled: z.boolean() }).strict();
export type BundleSpeech = z.infer<typeof bundleSpeechSchema>;
export const NO_SPEECH: BundleSpeech = { enabled: false };

/** What the platform reads aloud: a published guidance, or a feedback question. */
export const SPEECH_SOURCES = ['guidance', 'prompt'] as const;
export type SpeechSource = (typeof SPEECH_SOURCES)[number];
/** Route of the audio, under the platform address: `${SPEECH_PATH}/{source}/{id}`. */
export const SPEECH_PATH = '/api/v1/speech';

/** Everything the SDK needs, in one immutable read. Served with an ETag. */
export const configurationBundleSchema = z
  .object({
    schemaVersion: z.number().int().min(1),
    projectId: z.string().uuid(),
    version: z.number().int().min(0),
    activeLanguages: z.array(z.string().length(2)),
    /** Global kill switch — module M1. False on the suspension bundle. */
    enabled: z.boolean(),
    subsystems: bundleSubsystemsSchema,
    thresholds: z
      .object({
        hesitationMs: z.number().int().default(8000),
        rageClickCount: z.number().int().default(3),
        rageClickWindowMs: z.number().int().default(1000),
        rageClickRadiusPx: z.number().int().default(30),
        deadClickMs: z.number().int().default(2500),
      })
      .strict(),
    guidances: z.array(publishedGuidanceSchema),
    /** Feedback questions (module M4); a bundle published before phase 4 carries none. */
    feedback: bundleFeedbackSchema.default(NO_FEEDBACK),
    speech: bundleSpeechSchema.default(NO_SPEECH),
  })
  .strict();

export type ConfigurationBundle = z.infer<typeof configurationBundleSchema>;
